import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Radio, UserCircle, Save, Plus, X, Monitor, DollarSign, Image, Play, Layers, Eye } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_OPTIONS = ['Twitch', 'YouTube', 'Facebook Live', 'Kick', 'TikTok Live', 'Rumble'];
const CATEGORY_OPTIONS = ['Gaming', 'IRL', 'Music', 'Creative', 'Sports', 'Esports', 'Talk Shows', 'Education', 'Just Chatting', 'Cooking', 'Fitness', 'Tech'];

export default function StreamerProfile() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    displayName: '', bio: '', platforms: [], avgCCV: '', uniqueViewersMonthly: '', peakViewers: '',
    categories: [], schedule: '', avgHoursPerWeek: '', demographics: { ageRange: '', topGeos: '', gender: '' },
    customCpm: '', videoCpm: '', overlayCpm: '', holdingScreenRate: '', platformAudience: {}, autoAcceptAboveFloor: false,
  });
  const [newPlatform, setNewPlatform] = useState({ name: '', handle: '' });
  const [holdingPreview, setHoldingPreview] = useState(null);
  const [holdingBase64, setHoldingBase64] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarBase64, setAvatarBase64] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerBase64, setBannerBase64] = useState(null);
  const holdingRef = useRef(null);
  const avatarRef = useRef(null);
  const bannerRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/streamer-profiles/me`, { headers });
      if (res.data) {
        setProfile(res.data);
        setForm({
          displayName: res.data.displayName || '',
          bio: res.data.bio || '',
          platforms: res.data.platforms || [],
          avgCCV: res.data.avgCCV || '',
          uniqueViewersMonthly: res.data.uniqueViewersMonthly || '',
          peakViewers: res.data.peakViewers || '',
          categories: res.data.categories || [],
          schedule: res.data.schedule || '',
          avgHoursPerWeek: res.data.avgHoursPerWeek || '',
          demographics: {
            ageRange: res.data.demographics?.ageRange || '',
            topGeos: Array.isArray(res.data.demographics?.topGeos) ? res.data.demographics.topGeos.join(', ') : (res.data.demographics?.topGeos || ''),
            gender: res.data.demographics?.gender || '',
          },
          customCpm: res.data.customCpm || res.data.videoCpm || '',
          videoCpm: res.data.videoCpm || res.data.customCpm || '',
          overlayCpm: res.data.overlayCpm || '',
          holdingScreenRate: res.data.holdingScreenRate || '',
          platformAudience: res.data.platformAudience || {},
          autoAcceptAboveFloor: res.data.autoAcceptAboveFloor || false,
        });
        if (res.data.holdingImageUrl) {
          setHoldingPreview(res.data.holdingImageUrl.startsWith('http') ? res.data.holdingImageUrl : `${BACKEND_URL}${res.data.holdingImageUrl}`);
        }
        if (res.data.profileImageUrl) {
          setAvatarPreview(res.data.profileImageUrl.startsWith('http') ? res.data.profileImageUrl : `${BACKEND_URL}${res.data.profileImageUrl}`);
        }
        if (res.data.bannerImageUrl) {
          setBannerPreview(res.data.bannerImageUrl.startsWith('http') ? res.data.bannerImageUrl : `${BACKEND_URL}${res.data.bannerImageUrl}`);
        }
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/streamer-profiles`, {
        displayName: form.displayName,
        bio: form.bio,
        platforms: form.platforms,
        avgCCV: parseInt(form.avgCCV) || 0,
        uniqueViewersMonthly: parseInt(form.uniqueViewersMonthly) || 0,
        peakViewers: parseInt(form.peakViewers) || 0,
        categories: form.categories,
        schedule: form.schedule,
        avgHoursPerWeek: parseFloat(form.avgHoursPerWeek) || 0,
        demographics: {
          ageRange: form.demographics.ageRange,
          topGeos: form.demographics.topGeos.split(',').map(s => s.trim()).filter(Boolean),
          gender: form.demographics.gender,
        },
        customCpm: parseFloat(form.videoCpm) || null,
        videoCpm: parseFloat(form.videoCpm) || null,
        overlayCpm: parseFloat(form.overlayCpm) || null,
        holdingScreenRate: parseFloat(form.holdingScreenRate) || null,
        autoAcceptAboveFloor: form.autoAcceptAboveFloor,
        platformAudience: Object.fromEntries(Object.entries(form.platformAudience).filter(([k,v]) => v > 0)),
        holdingImageData: holdingBase64 || undefined,
        profileImageData: avatarBase64 || undefined,
        bannerImageData: bannerBase64 || undefined,
      }, { headers });
      toast.success('Profile saved!');
      fetchProfile();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const addPlatform = () => {
    if (!newPlatform.name) return;
    setForm(f => ({ ...f, platforms: [...f.platforms, { ...newPlatform }] }));
    setNewPlatform({ name: '', handle: '' });
  };

  const removePlatform = (i) => setForm(f => ({ ...f, platforms: f.platforms.filter((_, idx) => idx !== i) }));
  const toggleCategory = (cat) => setForm(f => ({ ...f, categories: f.categories.includes(cat) ? f.categories.filter(c => c !== cat) : [...f.categories, cat] }));

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button data-testid="back-to-home" variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white hover:bg-white/5 p-2"><ArrowLeft className="w-4 h-4" /></Button>
            <div className="flex items-center gap-2"><UserCircle className="w-4 h-4 text-blue-400" /><span className="font-heading text-lg font-bold text-white uppercase">Streamer Profile</span></div>
          </div>
          {profile && <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-mono text-xs">Floor CPM: ${profile.floorCpm?.toFixed(2)}</Badge>}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-8">
        {/* Banner + Avatar */}
        <section className="border border-white/10 rounded-lg bg-[#121212]/50 overflow-hidden">
          {/* Banner */}
          <div className="relative h-40 bg-[#0a0a0a] group cursor-pointer" onClick={() => bannerRef.current?.click()}>
            {bannerPreview ? (
              <img src={bannerPreview} alt="Banner" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#121212] to-[#0a0a0a]">
                <div className="text-center">
                  <Image className="w-6 h-6 text-gray-700 mx-auto mb-1" />
                  <p className="text-[10px] font-mono text-gray-600 uppercase">Click to upload banner</p>
                  <p className="text-[9px] text-gray-700">Recommended: 1500 x 500px</p>
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-xs text-white font-mono bg-black/60 px-3 py-1 rounded">Change Banner</span>
            </div>
            <input ref={bannerRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="upload-banner"
              onChange={(e) => { const f = e.target.files[0]; if (!f) return; setBannerPreview(URL.createObjectURL(f)); const r = new FileReader(); r.onload = (ev) => setBannerBase64(ev.target.result.split(',')[1]); r.readAsDataURL(f); }} />
          </div>
          {/* Avatar overlay */}
          <div className="px-6 -mt-10 pb-6 flex items-end gap-5">
            <div className="relative group cursor-pointer" onClick={() => avatarRef.current?.click()}>
              <div className="w-20 h-20 rounded-xl border-4 border-[#121212] bg-[#0a0a0a] overflow-hidden flex items-center justify-center">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <UserCircle className="w-10 h-10 text-gray-700" />
                )}
              </div>
              <div className="absolute inset-0 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-[9px] text-white font-mono">Edit</span>
              </div>
              <input ref={avatarRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="upload-avatar"
                onChange={(e) => { const f = e.target.files[0]; if (!f) return; setAvatarPreview(URL.createObjectURL(f)); const r = new FileReader(); r.onload = (ev) => setAvatarBase64(ev.target.result.split(',')[1]); r.readAsDataURL(f); }} />
            </div>
            <div className="pb-1">
              <p className="text-sm text-white font-medium">{form.displayName || 'Your Name'}</p>
              <p className="text-[10px] text-gray-500 font-mono">Click images to upload avatar + banner</p>
            </div>
          </div>
        </section>

        {/* Basic Info */}
        <section className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Basic Info</h3>
          <div className="space-y-4">
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Display Name</Label><Input data-testid="profile-display-name" value={form.displayName} onChange={e => setForm(f => ({...f, displayName: e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1" placeholder="Your stream name" /></div>
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Bio</Label><Textarea data-testid="profile-bio" value={form.bio} onChange={e => setForm(f => ({...f, bio: e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1 min-h-[80px]" placeholder="Tell brands about your content..." /></div>
          </div>
        </section>

        {/* Stats */}
        <section className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Audience Stats</h3>
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Avg CCV</Label><Input data-testid="profile-avg-ccv" type="number" value={form.avgCCV} onChange={e => setForm(f => ({...f, avgCCV: e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1" placeholder="500" /></div>
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Unique Viewers/Mo</Label><Input data-testid="profile-unique-viewers" type="number" value={form.uniqueViewersMonthly} onChange={e => setForm(f => ({...f, uniqueViewersMonthly: e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1" placeholder="10000" /></div>
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Peak Viewers</Label><Input data-testid="profile-peak-viewers" type="number" value={form.peakViewers} onChange={e => setForm(f => ({...f, peakViewers: e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1" placeholder="2000" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Schedule</Label><Input data-testid="profile-schedule" value={form.schedule} onChange={e => setForm(f => ({...f, schedule: e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1" placeholder="Mon-Fri 7pm EST" /></div>
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Avg Hours/Week</Label><Input data-testid="profile-hours" type="number" value={form.avgHoursPerWeek} onChange={e => setForm(f => ({...f, avgHoursPerWeek: e.target.value}))} className="bg-black/40 border-white/10 text-white mt-1" placeholder="25" /></div>
          </div>
        </section>

        {/* Platforms */}
        <section className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Platforms</h3>
          {form.platforms.length > 0 && (
            <div className="space-y-2 mb-4">
              {form.platforms.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-white/[0.03] rounded border border-white/5">
                  <Monitor className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-white font-medium">{p.name}</span>
                  {p.handle && <span className="text-xs text-gray-500 font-mono">@{p.handle}</span>}
                  <Button variant="ghost" size="sm" onClick={() => removePlatform(i)} className="ml-auto text-gray-500 hover:text-red-400 h-6 w-6 p-0"><X className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <select data-testid="platform-select" value={newPlatform.name} onChange={e => setNewPlatform(p => ({...p, name: e.target.value}))} className="bg-black/40 border border-white/10 text-white rounded px-3 py-2 text-sm flex-1">
              <option value="">Select platform</option>
              {PLATFORM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <Input value={newPlatform.handle} onChange={e => setNewPlatform(p => ({...p, handle: e.target.value}))} className="bg-black/40 border-white/10 text-white flex-1" placeholder="Handle" />
            <Button data-testid="add-platform-button" variant="ghost" onClick={addPlatform} className="text-gray-400 hover:text-white border border-white/10"><Plus className="w-4 h-4" /></Button>
          </div>
        </section>

        {/* Categories */}
        <section className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Content Categories</h3>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map(cat => (
              <button key={cat} data-testid={`category-${cat}`} onClick={() => toggleCategory(cat)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${form.categories.includes(cat) ? 'bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30' : 'bg-white/5 text-gray-400 border border-white/10 hover:border-white/20'}`}>
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* Platform Audience Numbers */}
        <section className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-2">Platform Reach</h3>
          <p className="text-xs text-gray-500 mb-4">How many concurrent viewers do you average on each platform? Brands use this to estimate total reach.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {['Twitch', 'YouTube', 'TikTok', 'Kick', 'Instagram', 'Facebook', 'Rumble', 'Whatnot'].map(plat => {
              const key = plat.toLowerCase();
              const colors = { twitch: '#9146FF', youtube: '#FF0000', tiktok: '#00F2EA', kick: '#53FC18', instagram: '#E4405F', facebook: '#1877F2', rumble: '#85C742', whatnot: '#FF6B35' };
              return (
                <div key={plat}>
                  <Label className="text-[10px] font-mono text-gray-500 uppercase tracking-widest flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors[key] }} />
                    {plat}
                  </Label>
                  <Input
                    data-testid={`audience-${key}`}
                    type="number"
                    value={form.platformAudience[key] || ''}
                    onChange={e => setForm(f => ({ ...f, platformAudience: { ...f.platformAudience, [key]: parseInt(e.target.value) || 0 } }))}
                    className="bg-black/40 border-white/10 text-white mt-1 h-9 text-sm"
                    placeholder="0"
                  />
                </div>
              );
            })}
          </div>
          {Object.values(form.platformAudience).some(v => v > 0) && (
            <div className="mt-4 p-3 border border-emerald-500/10 bg-emerald-500/5 rounded flex items-center justify-between">
              <span className="text-xs font-mono text-gray-400 uppercase">Total Multi-Platform Reach</span>
              <span className="text-lg font-bold text-emerald-400 font-mono">{Object.values(form.platformAudience).reduce((s, v) => s + (v || 0), 0).toLocaleString()}</span>
            </div>
          )}
        </section>

        {/* Demographics */}
        <section className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-4">Audience Demographics</h3>
          <div className="grid grid-cols-3 gap-4">
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Audience Gender</Label>
              <Select value={form.demographics.gender} onValueChange={v => setForm(f => ({...f, demographics: {...f.demographics, gender: v}}))}>
                <SelectTrigger data-testid="profile-gender" className="bg-black/40 border-white/10 text-white mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10"><SelectItem value="male" className="text-xs">Majority Male</SelectItem><SelectItem value="female" className="text-xs">Majority Female</SelectItem><SelectItem value="mixed" className="text-xs">Mixed</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Age Range</Label><Input data-testid="profile-age-range" value={form.demographics.ageRange} onChange={e => setForm(f => ({...f, demographics: {...f.demographics, ageRange: e.target.value}}))} className="bg-black/40 border-white/10 text-white mt-1" placeholder="18-34" /></div>
            <div><Label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Top Regions</Label><Input data-testid="profile-geos" value={form.demographics.topGeos} onChange={e => setForm(f => ({...f, demographics: {...f.demographics, topGeos: e.target.value}}))} className="bg-black/40 border-white/10 text-white mt-1" placeholder="US, UK, CA" /></div>
          </div>
        </section>

        {/* CPM Settings */}
        <section className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-2 flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-400" /> CPM Rates</h3>
          <p className="text-xs text-gray-500 mb-4">Set your asking CPM for each ad format. Brands bid at or above these rates.</p>
          
          {/* Video Ads */}
          <div className="p-4 border border-white/10 rounded-lg bg-white/[0.02] mb-4">
            <p className="text-xs font-bold text-white mb-3 flex items-center gap-2"><Play className="w-3.5 h-3.5 text-[#FF3B30]" /> Video Ad Slots</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">15s CPM</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <Input data-testid="profile-video-cpm" type="number" step="0.50" value={form.videoCpm} onChange={e => setForm(f => ({...f, videoCpm: e.target.value}))}
                    className="bg-black/40 border-white/10 text-white pl-7 h-10" placeholder="10.00" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">30s CPM (Auto 1.8x)</Label>
                <div className="bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-white font-mono mt-1 text-sm">
                  ${form.videoCpm ? (parseFloat(form.videoCpm) * 1.8).toFixed(2) : '--'}
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Slot Price (est.)</Label>
                <div className="bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-emerald-400 font-mono mt-1 text-sm">
                  ${form.videoCpm && form.avgCCV ? ((parseInt(form.avgCCV) * parseFloat(form.videoCpm)) / 1000).toFixed(2) : '--'}
                </div>
              </div>
            </div>
          </div>

          {/* Overlay Ads */}
          <div className="p-4 border border-[#7C3AED]/20 rounded-lg bg-[#7C3AED]/[0.02] mb-4">
            <p className="text-xs font-bold text-white mb-3 flex items-center gap-2"><Layers className="w-3.5 h-3.5 text-[#7C3AED]" /> Overlay / Banner Ads</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Overlay CPM</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <Input data-testid="profile-overlay-cpm" type="number" step="0.50" value={form.overlayCpm} onChange={e => setForm(f => ({...f, overlayCpm: e.target.value}))}
                    className="bg-black/40 border-white/10 text-white pl-7 h-10" placeholder="3.00" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Per Display (est.)</Label>
                <div className="bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-[#7C3AED] font-mono mt-1 text-sm">
                  ${form.overlayCpm && form.avgCCV ? ((parseInt(form.avgCCV) * parseFloat(form.overlayCpm)) / 1000).toFixed(4) : '--'}
                </div>
              </div>
            </div>
            <p className="text-[9px] text-gray-600 mt-2">Overlay ads are priced per 1,000 impressions. Each display = your CCV in impressions.</p>
          </div>

          {/* Holding Screen Sponsorship */}
          <div className="p-4 border border-emerald-500/20 rounded-lg bg-emerald-500/[0.02] mb-4">
            <p className="text-xs font-bold text-white mb-3 flex items-center gap-2"><Monitor className="w-3.5 h-3.5 text-emerald-400" /> Holding Screen Sponsorship</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Rate Per Stream</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <Input data-testid="profile-holding-rate" type="number" step="5" value={form.holdingScreenRate} onChange={e => setForm(f => ({...f, holdingScreenRate: e.target.value}))}
                    className="bg-black/40 border-white/10 text-white pl-7 h-10" placeholder="50.00" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">Exclusivity</Label>
                <div className="bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-emerald-400 font-mono mt-1 text-sm">
                  1 sponsor only
                </div>
              </div>
            </div>
            <p className="text-[9px] text-gray-600 mt-2">Brands pay per stream for their HTML/image to be your holding screen between ad breaks. Only one sponsor allowed at a time.</p>
          </div>

          {/* Auto-Accept Toggle */}
          <div className="mt-5 p-4 border border-white/10 rounded-lg bg-white/[0.02]">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="text-sm text-white font-medium">Auto-Accept Bids at Floor or Above</p>
                <p className="text-xs text-gray-500 mt-0.5">Bids at or above your CPM floor will automatically go into your ad queue without manual review</p>
              </div>
              <button
                data-testid="auto-accept-toggle"
                onClick={() => setForm(f => ({ ...f, autoAcceptAboveFloor: !f.autoAcceptAboveFloor }))}
                className={`w-12 h-7 rounded-full transition-all flex-shrink-0 ml-4 ${form.autoAcceptAboveFloor ? 'bg-emerald-500' : 'bg-white/10'}`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${form.autoAcceptAboveFloor ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </label>
            {form.autoAcceptAboveFloor && (
              <p className="text-[10px] text-emerald-400 font-mono mt-2">Bids below your floor will still require manual review.</p>
            )}
          </div>
        </section>
        <section className="border border-white/10 rounded-lg bg-[#121212]/50 p-6">
          <h3 className="font-heading text-lg font-bold text-white mb-2 flex items-center gap-2"><Image className="w-5 h-5 text-blue-400" /> Holding Screen</h3>
          <p className="text-xs text-gray-500 mb-4">This displays when you cut to commercial with no video ads, or between ad breaks. Upload a PNG/JPEG for a static BRB screen.</p>
          {holdingPreview && (
            <div className="mb-4">
              <div className="aspect-video bg-black rounded-lg overflow-hidden border border-white/10 max-w-lg relative group">
                <img src={holdingPreview} alt="Holding" className="w-full h-full object-contain" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button size="sm" variant="ghost" className="text-white bg-white/20 hover:bg-white/30"
                    onClick={() => window.open(holdingPreview, '_blank', 'width=960,height=540')}>
                    <Eye className="w-4 h-4 mr-1" /> Full Preview
                  </Button>
                </div>
              </div>
              <p className="text-[9px] text-gray-600 font-mono mt-1">Hover to preview full size. This is what viewers see between ad breaks.</p>
            </div>
          )}
          {/* Show brand-sponsored holding screen if active */}
          {!holdingPreview && (
            <div className="mb-4 p-4 border border-dashed border-white/10 rounded-lg bg-white/[0.01] text-center">
              <Monitor className="w-8 h-8 text-gray-700 mx-auto mb-2" />
              <p className="text-xs text-gray-500">No holding screen set. Upload one below, or a brand can sponsor your holding screen.</p>
            </div>
          )}
          <input ref={holdingRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => {
            const file = e.target.files[0];
            if (!file) return;
            setHoldingPreview(URL.createObjectURL(file));
            const reader = new FileReader();
            reader.onload = (ev) => setHoldingBase64(ev.target.result.split(',')[1]);
            reader.readAsDataURL(file);
          }} />
          <Button data-testid="upload-holding-image" variant="ghost" onClick={() => holdingRef.current?.click()}
            className="text-gray-400 hover:text-white border border-white/10 hover:bg-white/5">
            <Image className="w-4 h-4 mr-2" /> {holdingPreview ? 'Change Image' : 'Upload Holding Image'}
          </Button>
        </section>

        <Button data-testid="save-profile-button" onClick={handleSave} disabled={saving} className="w-full h-14 bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold uppercase tracking-widest rounded-none text-lg">
          <Save className="w-5 h-5 mr-2" /> {saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </main>
    </div>
  );
}
